import React, { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, UserPlus, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useContactSearch } from '@/hooks/useContactSearch';

export interface ContactOption {
  id: string;
  first_name: string | null;
  last_name: string | null;
  primary_email: string | null;
  primary_phone: string | null;
}

interface SlotContactSelectorProps {
  value: ContactOption | null;
  onSelectContact: (contact: ContactOption) => void;
  onCreateNew: () => void;
  dealContact?: ContactOption | null;
  placeholder?: string;
  excludeContactIds?: string[];
}

export function SlotContactSelector({
  value,
  onSelectContact,
  onCreateNew,
  dealContact,
  placeholder = 'Selecionar contato...',
  excludeContactIds = [],
}: SlotContactSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { contacts: searchResults, loading } = useContactSearch(searchQuery, 20);

  // Filter out excluded contacts and deal contact from search results
  const filteredResults = useMemo(() => {
    const excludeSet = new Set(excludeContactIds);
    if (dealContact?.id) excludeSet.add(dealContact.id);
    return searchResults.filter((c) => !excludeSet.has(c.id));
  }, [searchResults, excludeContactIds, dealContact]);

  const getDisplayName = (contact: ContactOption | null) => {
    if (!contact) return null;
    const first = contact.first_name || '';
    const last = contact.last_name || '';
    return `${first} ${last}`.trim() || contact.primary_email || 'Sem nome';
  };

  const getInitials = (contact: ContactOption | null) => {
    if (!contact) return '?';
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getSubtitle = (contact: ContactOption | null) => {
    if (!contact) return null;
    return contact.primary_email || contact.primary_phone || null;
  };

  const handleSelect = (contact: ContactOption) => {
    onSelectContact(contact);
    setOpen(false);
    setSearchQuery('');
  };

  const handleCreateNew = () => {
    onCreateNew();
    setOpen(false);
    setSearchQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 py-2"
        >
          {value ? (
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getInitials(value)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-start text-left">
                <span className="font-medium text-sm">{getDisplayName(value)}</span>
                {getSubtitle(value) && (
                  <span className="text-xs text-muted-foreground">{getSubtitle(value)}</span>
                )}
              </div>
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nome ou email..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList 
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <CommandEmpty>
              {loading ? 'Buscando...' : 'Nenhum contato encontrado'}
            </CommandEmpty>

            {/* Deal Contact Group - always first */}
            {dealContact && (
              <CommandGroup heading="Contato do Deal">
                <CommandItem
                  value={`deal-${dealContact.id}`}
                  onSelect={() => handleSelect(dealContact)}
                  className="cursor-pointer"
                >
                  <Avatar className="h-8 w-8 mr-3">
                    <AvatarFallback className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                      {getInitials(dealContact)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col flex-1">
                    <span className="font-medium">{getDisplayName(dealContact)}</span>
                    {getSubtitle(dealContact) && (
                      <span className="text-xs text-muted-foreground">
                        {getSubtitle(dealContact)}
                      </span>
                    )}
                  </div>
                  {value?.id === dealContact.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </CommandItem>
              </CommandGroup>
            )}

            {/* Search Results */}
            {searchQuery.length >= 1 && filteredResults.length > 0 && (
              <CommandGroup heading="Resultados da Busca">
                {filteredResults.map((contact) => (
                  <CommandItem
                    key={contact.id}
                    value={contact.id}
                    onSelect={() => handleSelect(contact)}
                    className="cursor-pointer"
                  >
                    <Avatar className="h-8 w-8 mr-3">
                      <AvatarFallback className="text-xs bg-muted">
                        {getInitials(contact)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col flex-1">
                      <span className="font-medium">{getDisplayName(contact)}</span>
                      {getSubtitle(contact) && (
                        <span className="text-xs text-muted-foreground">
                          {getSubtitle(contact)}
                        </span>
                      )}
                    </div>
                    {value?.id === contact.id && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            <CommandSeparator />

            {/* Create New Option - always at the end */}
            <CommandGroup>
              <CommandItem
                onSelect={handleCreateNew}
                className="cursor-pointer text-primary"
              >
                <div className="flex items-center gap-3 w-full py-1">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <UserPlus className="h-4 w-4 text-primary" />
                  </div>
                  <span className="font-medium">Criar novo contato</span>
                </div>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
